import { action, observable } from "mobx";
import { DisplayMode } from "../constants/DisplayMode.enum";
import { isLocalhost } from "../env";
import { removeFromArray } from "../utils/array.utils";
import { breakpoint } from "../utils/breakpoints.utils";
import { multiExpressionReaction, useStore } from "../utils/mobx.utils";
import { isArray } from "../utils/typeChecks.utils";
import { useControllers } from "./controller.hooks";
import { useOnMount } from "./lifecycle.hooks";
import { useResizeObserver } from "./useResizeObserver";
var RESIZE_QUERIES = observable([]);
// @ts-ignore
if (isLocalhost)
    window.RESIZE_QUERIES = RESIZE_QUERIES;
export var useResizeQuery = function (ref, defaultWidth) {
    var UI = useControllers().UI;
    var s = useStore(function () { return ({
        ref: ref,
        entry: null,
        boundingBox: null,
        get ready() {
            return Boolean(s.entry);
        },
        get width() {
            var _a, _b, _c;
            return ((_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.target) === null || _b === void 0 ? void 0 : _b.clientWidth) || ((_c = ref.current) === null || _c === void 0 ? void 0 : _c.clientWidth) || defaultWidth || 0;
        },
        get height() {
            var _a, _b, _c, _d, _e;
            return (_e = (_c = (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.target) === null || _b === void 0 ? void 0 : _b.clientHeight) !== null && _c !== void 0 ? _c : (_d = ref.current) === null || _d === void 0 ? void 0 : _d.clientHeight) !== null && _e !== void 0 ? _e : 0;
        },
        get ratio() {
            return s.width / s.height;
        },
        get contentBottom() {
            var _a, _b;
            return (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.contentRect.bottom) !== null && _b !== void 0 ? _b : 0;
        },
        get contentHeight() {
            var _a, _b;
            return (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.contentRect.height) !== null && _b !== void 0 ? _b : 0;
        },
        get contentLeft() {
            var _a, _b;
            return (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.contentRect.left) !== null && _b !== void 0 ? _b : 0;
        },
        get contentRight() {
            var _a, _b;
            return (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.contentRect.right) !== null && _b !== void 0 ? _b : 0;
        },
        get contentTop() {
            var _a, _b;
            return (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.contentRect.top) !== null && _b !== void 0 ? _b : 0;
        },
        get contentWidth() {
            var _a, _b;
            return (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.contentRect.width) !== null && _b !== void 0 ? _b : 0;
        },
        get scrollWidth() {
            var _a, _b, _c;
            return (_c = (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.target) === null || _b === void 0 ? void 0 : _b.scrollWidth) !== null && _c !== void 0 ? _c : s.width;
        },
        get scrollHeight() {
            var _a, _b, _c;
            return (_c = (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.target) === null || _b === void 0 ? void 0 : _b.scrollHeight) !== null && _c !== void 0 ? _c : s.height;
        },
        get x() {
            var _a, _b;
            return (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.contentRect.x) !== null && _b !== void 0 ? _b : 0;
        },
        get y() {
            var _a, _b;
            return (_b = (_a = s.entry) === null || _a === void 0 ? void 0 : _a.contentRect.y) !== null && _b !== void 0 ? _b : 0;
        },
        get top() {
            var _a;
            return (_a = s.boundingBox) === null || _a === void 0 ? void 0 : _a.top;
        },
        get rightFromViewportLeft() {
            var _a;
            return (_a = s.boundingBox) === null || _a === void 0 ? void 0 : _a.right;
        },
        get rightFromViewportRight() {
            if (s.rightFromViewportLeft === undefined)
                return undefined;
            return UI.vw - s.rightFromViewportLeft;
        },
        get bottomFromViewportTop() {
            var _a;
            return ((_a = s.boundingBox) === null || _a === void 0 ? void 0 : _a.bottom) || 0;
        },
        get bottomFromViewportBottom() {
            if (s.bottomFromViewportTop === undefined)
                return undefined;
            return UI.vh - s.bottomFromViewportTop;
        },
        get left() {
            var _a, _b;
            return (_b = (_a = s.boundingBox) === null || _a === void 0 ? void 0 : _a.left) !== null && _b !== void 0 ? _b : s.contentLeft;
        },
        fromBreakpoint: function (n) {
            return s.width >= breakpoint(n);
        },
        get displayMode() {
            switch (true) {
                case s.onlyPhones: return DisplayMode.phone;
                case s.onlyTablets: return DisplayMode.tablet;
                default: return DisplayMode.desktop;
            }
        },
        get onlyPhones() {
            return s.width < breakpoint('tablet');
        },
        get fromPhoneMd() {
            return s.width >= breakpoint('phone-md');
        },
        get fromPhoneLg() {
            return s.width >= breakpoint('phone-lg');
        },
        get onlyTablets() {
            return s.width >= breakpoint('tablet') && s.width < breakpoint('desktop');
        },
        get fromTablet() {
            return s.width >= breakpoint('tablet');
        },
        get fromTabletLg() {
            return s.width >= breakpoint('tablet-lg');
        },
        get fromDesktop() {
            return s.width >= breakpoint('desktop');
        },
        get fromDesktopLg() {
            return s.width >= breakpoint('desktop-lg');
        },
        callbacks: [],
        onResize: action(function (fn) {
            s.callbacks.push(fn);
        }),
        removeHandler: action(function (fn) {
            s.callbacks.splice(s.callbacks.indexOf(fn), 1);
        }),
        updateBoundingBox: action(function () {
            if (s.entry)
                s.boundingBox = s.entry.target.getBoundingClientRect();
        })
    }); });
    useResizeObserver(function (entries) {
        // We wrap it in requestAnimationFrame to avoid this error - ResizeObserver loop limit exceeded
        window.requestAnimationFrame(action(function () {
            if (!isArray(entries) || !entries.length)
                return;
            s.entry = entries[0];
            s.updateBoundingBox();
        }));
    }, ref);
    useOnMount(function () {
        var handler = function () { return s.updateBoundingBox(); };
        window.addEventListener('resize', handler);
        if (isLocalhost)
            RESIZE_QUERIES.push(s);
        var disposer = multiExpressionReaction([
            function () { return s.width; },
            function () { return s.height; },
        ], function () {
            s.callbacks.forEach(function (c) { return c(); });
        });
        return function () {
            window.removeEventListener('resize', handler);
            if (isLocalhost)
                removeFromArray(RESIZE_QUERIES, s);
            disposer();
        };
    });
    return s;
};
